import { inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { concatLatestFrom } from "@ngrx/operators";
import { Store } from "@ngrx/store";
import { TagsGQL, TagsQueryVariables, SetTagsOnObjectGQL, SetTagsOnObjectMutationVariables, BaseTagFragment } from 'graphql.generated';
import { catchError, exhaustMap, map, mergeMap, of, tap, filter, switchMap } from 'rxjs';

import { TAG_TYPES } from '../../../interfaces/tag';

import { createConfigSelector } from "../../../state/branding.store";
import { selectJob, selectJobId } from "../../job-tool.reducer";

import { JobTagActions } from "./job-tags.actions";
import { jobTagsFeature } from "./job-tags.feature";

export const loadJobTags = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
  return actions$.pipe(
    ofType(JobTagActions.inPlaceActivated),
    concatLatestFrom(() => store.select(selectJob)),
    map(([ _action, job ]) => JobTagActions.jobTagsLoaded({ tags: job.tags })),
  );
}, { functional: true, dispatch: true });

export const searchTags = createEffect((
  actions$ = inject(Actions),
  tagsGQL = inject(TagsGQL),
) => {
  return actions$.pipe(
    ofType(JobTagActions.tagAutocompleteSearchFired),
    filter(({ query }) => query?.trim()?.length > 0),
    mergeMap((action) => of(action).pipe(
      map(({ query }): { vars: TagsQueryVariables } => {
        return {
          vars: {
            filter: {
              search: query,
              limit: 5,
              objectTypes: [TAG_TYPES.Job],
            },
            order: 'order:asc',
          }
        };
      }),
      exhaustMap(({ vars }) => tagsGQL.fetch(vars).pipe(
        map((res) => JobTagActions.searchTagsSuccess({
          tagSuggestions: res.data.tags.tags,
        })),
      )),
      catchError((error) => of(JobTagActions.searchTagsError({ error }))),
    )),
  );
}, { functional: true, dispatch: true });

export const setTags = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  setTagsOnObject = inject(SetTagsOnObjectGQL),
) => {
  return actions$.pipe(
    ofType(JobTagActions.inPlaceDeactivated),
    mergeMap((action) => of(action).pipe(
      concatLatestFrom(() => [
        store.select(jobTagsFeature.selectTags),
        store.select(jobTagsFeature.selectTagsModified),
        store.select(selectJobId),
      ]),
      filter(([_action, _tags, tagsModified ]) => tagsModified),
      map(([_action, tags, _modified, jobId]): {
      vars: SetTagsOnObjectMutationVariables;
      tags: BaseTagFragment[];
      jobId: string;
      } => {
        return {
          vars: {
            tags: tags.map((tag) => ({ tagId: tag.id, private: false })),
              objectId: jobId,
            objectLabel: TAG_TYPES.Job,
          },
          tags,
          jobId,
        };
      }),
      exhaustMap(({ vars, tags, jobId }) => setTagsOnObject.mutate(vars).pipe(
        tap((result) => {

          const isSuccess = result.data?.tags.setObjectTags;

          if (!isSuccess) {
            throw new Error('Error assigning tags to job');
          }
        }),
        map(() => JobTagActions.setTagsSuccess({
          tags,
          jobId,
        })),
      )),
      catchError((error) => of(JobTagActions.setTagsError({ error }))),
    )),
  );
}, { functional: true, dispatch: true });

export const fetchDefaultTags = createEffect((
  store = inject(Store),
  tagsGQL = inject(TagsGQL),
) => {
  return store.select(createConfigSelector('TAGS_DEFAULTTAGNAMES')).pipe(
    filter((defaultTagNames) => defaultTagNames && typeof defaultTagNames === 'string'),
    map((defaultTagNames) => defaultTagNames.split(',')),
    filter((defaultTagNames) => Array.isArray(defaultTagNames) && defaultTagNames.length > 0),
    mergeMap((defaultTagNames) => of(null).pipe(
      map((): TagsQueryVariables => ({
        filter: {
          names: defaultTagNames,
          limit: -1,
          objectTypes: [TAG_TYPES.Job],
        },
      })),
      switchMap((vars) => tagsGQL.fetch(vars).pipe(
        map((res) => JobTagActions.fetchDefaultTagsSuccess({
          defaultTags: res.data.tags.tags,
        })),
      )),
      catchError((error) => of(JobTagActions.setTagsError({ error }))),
    )),
  );
}, { functional: true, dispatch: true });
